import { useEffect, useRef, type ReactNode } from 'react';
import { createGalaxy } from '../js/galaxy.js';

export interface GalaxyProps {
  /** Fill the nearest positioned ancestor instead of sitting behind the whole page. */
  contained?: boolean;
  className?: string;
  /** Layers over the stars: .galaxy__glow, .galaxy__grid, art such as a planet <img>. */
  children?: ReactNode;
  /** The same sky every time (any integer); read once, when it mounts. */
  seed?: number;
  /** One still frame, for pictures of it (screenshots): no animation. Read once, when it mounts. */
  still?: boolean;
}

/** The animated galaxy background (js/galaxy.js on a canvas). One per page. */
export function Galaxy({ contained = false, className, children, seed, still = false }: GalaxyProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const galaxy = createGalaxy(canvasRef.current!, { seed, still });
    return () => galaxy.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the sky is made once
  }, []);

  const classes = ['galaxy', contained && 'galaxy--contained', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <canvas ref={canvasRef} className="galaxy__canvas" aria-hidden="true" />
      {children}
    </div>
  );
}
